import { Injectable, Type } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/** MDI 설정. np-mdi를 선언할 때 [config]로 넘긴다. 넘기지 않은 항목은 기본 설정을 쓴다. */
export interface NpMdiConfig {
  /** 최대 탭 수. 가득 차면 새 창을 열지 않고 알림을 띄운다. (자동으로 닫지 않는다.) 없으면 20, 0이면 무제한 */
  max?: number;
}

/** 기본 설정 */
export const NP_MDI_DEFAULT_CONFIG: Required<NpMdiConfig> = {
  max: 20,
};

/** 화면을 열 때 여는 쪽이 넘기는 값 */
export interface NpMdiOpenOptions {
  /** 탭 식별 키. 여는 쪽이 만든다. 같은 키가 열려 있으면 새로 열지 않고 그 탭으로 이동 */
  key: string;
  title: string;
  icon?: string;
  component: Type<any>;
  /** 컴포넌트 입력값 */
  inputs?: Record<string, any>;
}

/** 열린 탭 하나 (MDI 내부용) */
export interface NpMdiTab {
  id: number;
  key: string;
  title: string;
  icon: string;
  component: Type<any>;
  inputs?: Record<string, any>;
  openedAt: Date;
  ref: NpMdiTabRef;
}

/** 열린 탭의 참조용 정보. MDI 밖으로는 이것만 나간다. (getMdiData, closed, dismissed) */
export interface NpMdiData {
  /** 탭 식별 키 (여는 쪽이 만든 값) */
  key: string;
  title: string;
  /** 열 때 넘긴 컴포넌트 입력값 */
  inputs?: Record<string, any>;
}

/** 여는 쪽이 받는 탭 핸들. 같은 키로 다시 열면 같은 ref가 돌아온다. */
export class NpMdiTabRef {

  private readonly closedSubject = new Subject<void>();
  /** 탭이 없어지면 한 번 발생 (닫기, 취소 모두) */
  readonly closed: Observable<void> = this.closedSubject.asObservable();

  /** 탭 키. MDI의 update로 키가 바뀌면 같이 바뀐다 */
  constructor(public key: string, private mdi: NpMdiService) { }

  activate() {
    this.mdi.activateByKey(this.key);
  }

  close() {
    this.mdi.closeByKey(this.key);
  }

  dismiss() {
    this.mdi.dismissByKey(this.key);
  }

  /** @internal MDI가 탭을 닫을 때 호출 */
  notifyClosed() {
    this.closedSubject.next();
    this.closedSubject.complete();
  }
}

/**
 * MDI 탭 관리.
 * <p>
 * NpMdiComponent의 providers로 등록되어 np-mdi마다 하나씩 생긴다.
 * np-mdi 안의 화면(탭으로 열린 화면, Home 안의 컴포넌트)은 이 서비스를 주입받아 다른 화면을 탭으로 연다.
 * np-mdi를 가진 페이지는 주입받을 수 없으므로 NpMdiComponent를 가져와서 쓴다.
 * <p>
 * Home(np-mdi-home)은 탭 목록에 없다. activeId가 null이면 Home이 보이는 상태이다.
 */
@Injectable()
export class NpMdiService {

  /** 현재 설정 (기본 설정 + np-mdi의 [config]) */
  config: Required<NpMdiConfig> = { ...NP_MDI_DEFAULT_CONFIG };

  /** 열린 탭 목록 */
  tabs: NpMdiTab[] = [];
  /** 활성 탭 id. null이면 Home */
  activeId: number | null = null;

  /** 탭 목록이나 활성 탭이 바뀔 때 (MDI가 탭 바 스크롤 처리) */
  readonly changed$ = new Subject<void>();

  /** 화면이 닫기를 요청해서(embedClose) 탭이 닫힐 때 (닫힌 탭의 정보) */
  readonly closed$ = new Subject<NpMdiData>();

  /** 그 외의 경우로 탭이 닫힐 때: 화면의 취소(embedDismiss), 탭의 X, 모두 닫기 (닫힌 탭의 정보) */
  readonly dismissed$ = new Subject<NpMdiData>();

  private tabSeq = 0;

  get active(): NpMdiTab | undefined {
    return this.tabs.find(t => t.id === this.activeId);
  }

  /** 설정을 적용한다. 넘기지 않은 항목은 기본 설정으로 돌아간다. */
  configure(config?: NpMdiConfig) {
    this.config = {
      max: config?.max ?? NP_MDI_DEFAULT_CONFIG.max,
    };
  }

  /** 열린 탭 목록의 참조용 정보(키, 타이틀, 입력값). 복사본이라 고쳐도 MDI에는 영향이 없다. */
  getMdiData(): NpMdiData[] {
    return this.tabs.map(t => this.toMdiData(t));
  }

  private toMdiData(tab: NpMdiTab): NpMdiData {
    return { key: tab.key, title: tab.title, inputs: tab.inputs ? { ...tab.inputs } : undefined };
  }

  find(key: string): NpMdiTab | undefined {
    return this.tabs.find(t => t.key === key);
  }

  isOpen(key: string): boolean {
    return !!this.find(key);
  }

  /**
   * 화면을 탭으로 연다. 같은 키가 열려 있으면 새로 열지 않고 그 탭으로 이동한다.
   * <p>
   * 최대 탭 수(config.max)까지 차 있으면 열지 않고 알림을 띄운다. 열려 있는 탭을 자동으로 닫지 않는다.
   *
   * @return 탭 핸들. 열지 못했으면 undefined
   */
  open(options: NpMdiOpenOptions): NpMdiTabRef | undefined {
    const exist = this.find(options.key);
    if (exist) {
      this.activate(exist.id);
      return exist.ref;
    }

    // max가 0이면 무제한
    const max = this.config.max;
    if (max > 0 && this.tabs.length >= max) {
      alert(`더 이상 새 창을 열 수 없습니다. (최대 ${max}개)\n열려 있는 창을 닫은 뒤 다시 시도하세요.`);
      return undefined;
    }

    const tab: NpMdiTab = {
      id: ++this.tabSeq,
      key: options.key,
      title: options.title,
      icon: options.icon ?? 'fa-window-maximize',
      component: options.component,
      inputs: options.inputs,
      openedAt: new Date(),
      ref: new NpMdiTabRef(options.key, this),
    };
    this.tabs.push(tab);
    this.activate(tab.id);
    return tab.ref;
  }

  /**
   * 열린 탭의 키·타이틀을 바꾼다. 탭 안 화면이 보여 주는 대상이 바뀌었을 때 쓴다.
   * <p>
   * 바꿀 키가 다른 탭에 이미 열려 있으면 키는 그대로 두고 타이틀만 바꾼다.
   *
   * @return 바꾼 뒤의 키. 탭이 없으면 key 그대로
   */
  update(key: string, changes: { key?: string; title?: string }): string {
    const tab = this.find(key);
    if (!tab) return key;

    if (changes.key && changes.key !== key && !this.isOpen(changes.key)) {
      tab.key = changes.key;
      tab.ref.key = changes.key;
    }
    if (changes.title) tab.title = changes.title;
    this.changed$.next();
    return tab.key;
  }

  activate(id: number) {
    if (!this.tabs.some(t => t.id === id)) return;
    this.activeId = id;
    this.changed$.next();
  }

  activateByKey(key: string) {
    const tab = this.find(key);
    if (tab) this.activate(tab.id);
  }

  activateHome() {
    this.activeId = null;
    this.changed$.next();
  }

  /** 탭을 닫는다. (closed$) 화면이 닫기를 요청했을 때(embedClose) 쓴다. */
  close(id: number) {
    this.remove(id, this.closed$);
  }

  /** 탭을 닫는다. (dismissed$) 화면의 취소(embedDismiss), 탭의 X 등에 쓴다. */
  dismiss(id: number) {
    this.remove(id, this.dismissed$);
  }

  closeByKey(key: string) {
    const tab = this.find(key);
    if (tab) this.close(tab.id);
  }

  dismissByKey(key: string) {
    const tab = this.find(key);
    if (tab) this.dismiss(tab.id);
  }

  /** 탭을 모두 닫는다. (dismissed$) exceptId를 주면 그 탭은 남긴다. */
  dismissAll(exceptId?: number) {
    this.tabs
      .filter(t => t.id !== exceptId)
      .forEach(t => this.dismiss(t.id));
  }

  /** 기준 탭의 오른쪽 탭을 모두 닫는다. (dismissed$) */
  dismissRightOf(id: number) {
    const idx = this.tabs.findIndex(t => t.id === id);
    if (idx < 0) return;
    this.tabs.slice(idx + 1).forEach(t => this.dismiss(t.id));
  }

  /** 탭을 없앤다. 오른쪽 탭, 없으면 왼쪽 탭, 그것도 없으면 Home을 활성화 */
  private remove(id: number, notify$: Subject<NpMdiData>) {
    const idx = this.tabs.findIndex(t => t.id === id);
    const tab = this.tabs[idx];
    if (!tab) return;

    this.tabs.splice(idx, 1);
    if (this.activeId === id) {
      const next = this.tabs[idx] ?? this.tabs[idx - 1];
      this.activeId = next?.id ?? null;
    }
    tab.ref.notifyClosed();
    notify$.next(this.toMdiData(tab));
    this.changed$.next();
  }

}
